export type Tc6ReferenceKey =
  | 'soares_pereira_2011'
  | 'iwama'
  | 'britto_1'
  | 'britto_2'
  | 'dourado'
  | 'troosters'
  | 'enright_sherrill';

export type Tc6WorkflowStatus = 'awaiting_second_attempt' | 'completed';
export type Tc6StopReason = 'patient_request' | 'supervisor_interruption';

export interface Tc6ChartImages {
  attempt1_distance_payload?: string;
  attempt1_vitals_payload?: string;
  attempt2_distance_payload?: string;
  attempt2_vitals_payload?: string;
}

export interface Tc6MinuteReading {
  minute: number;
  hr: string | null;
  spo2: string | null;
}

export interface Tc6BaselineFinalBlock {
  hr: string | null;
  spo2: string | null;
  bp: {
    sys: string | null;
    dia: string | null;
  };
  borg: {
    dyspnea: number | null;
    fatigue: number | null;
  };
}

export interface Tc6PatientSnapshot {
  age: number | null;
  sex: string | null;
  height: number | null;
  weight: number | null;
  bmi: number | null;
}

export interface Tc6DerivedMetrics {
  predictedDistance_m: number | null;
  predictedFormula?: string;
  percentPredicted: number | null;
  sixMinuteWork_kgm: number | null;
  sixMinuteWork_kJ: number | null;
  meanSpeed_kmh: number | null;
  distancePerMinute_m: number | null;
  vo2Estimate_mlkgmin: number | null;
  metsEstimate: number | null;
  duration_s: number;
}

export interface Tc6StopInterval {
  from_s: number;
  to_s: number;
  duration_s: number;
  from_fmt: string;
  to_fmt: string;
  duration_fmt: string;
  reason: Tc6StopReason | null;
  reason_label: string | null;
}

export interface Tc6StopReport {
  total_stopped_s: number;
  total_stopped_fmt: string;
  intervals: Tc6StopInterval[];
}

export interface Tc6TimerBlock {
  elapsed_s: number;
  completed: boolean;
  startedAt: string | null;
  endedAt: string | null;
}

export interface Tc6Speeds {
  avg_m_min: number;
  avg_m_s: number;
  avg_km_h: number;
}

export interface Tc6AttemptPayload {
  corridorLength_m: number;
  laps: number;
  last_lap_meters: number;
  total_distance_m: number;
  derivedMetrics: Tc6DerivedMetrics | null;
  baseline: Tc6BaselineFinalBlock;
  per_minute: Tc6MinuteReading[];
  per_minute_distance_m: number[];
  per_minute_speed_kmh: number[];
  per_minute_segments_m: number[];
  lap_totals_by_minute?: number[];
  extra_by_minute?: number[];
  final: Tc6BaselineFinalBlock;
  stop_report: Tc6StopReport;
  timer: Tc6TimerBlock;
  patientSnapshot: Tc6PatientSnapshot | null;
  tc6Speeds?: Tc6Speeds | null;
  finishedAt: string;
}

export interface Tc6WorkflowBlock {
  status: Tc6WorkflowStatus;
  attemptCount: 1 | 2;
  savedAt: string;
}

export interface Tc6ResultPayload {
  referenceEquation: Tc6ReferenceKey;
  report?: {
    interpretation?: string;
  };
  workflow: Tc6WorkflowBlock;
  attempt1?: Tc6AttemptPayload;
  attempt2?: Tc6AttemptPayload;
  finishedAt?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
}

export interface Tc6ReferenceResult {
  key: Tc6ReferenceKey;
  label: string;
  predicted: number | null;
  lln: number | null;
  percentPredicted: number | null;
  belowLLN: boolean | null;
  llnDescription: string;
}

export interface Tc6Summary {
  attemptCount: 1 | 2;
  workflowStatus: Tc6WorkflowStatus;
  bestAttempt: 1 | 2 | null;
  bestDistance_m: number | null;
  selectedReferenceKey: Tc6ReferenceKey;
  selectedReferenceLabel: string;
  predictedDistance_m: number | null;
  percentPredicted: number | null;
  meanSpeed_kmh: number | null;
  minSpo2: number | null;
  spo2Drop: number | null;
  totalStopped_s: number | null;
  hasSignificantDesaturation: boolean;
  severity: string;
}

type MinuteData = {
  laps: number;
  extraMeters: number;
};

const DEFAULT_TC6_REFERENCE_KEY: Tc6ReferenceKey = 'soares_pereira_2011';

const STOP_REASON_LABELS: Record<Tc6StopReason, string> = {
  patient_request: 'Parou por solicitação do paciente',
  supervisor_interruption: 'Interrompido pelo avaliador',
};

export const TC6_REFERENCE_OPTIONS: Array<{ key: Tc6ReferenceKey; label: string }> = [
  { key: 'soares_pereira_2011', label: 'Soares & Pereira 2011' },
  { key: 'iwama', label: 'Iwama et al.' },
  { key: 'britto_1', label: 'Britto et al. 1' },
  { key: 'britto_2', label: 'Britto et al. 2' },
  { key: 'dourado', label: 'Dourado et al.' },
  { key: 'troosters', label: 'Troosters et al.' },
  { key: 'enright_sherrill', label: 'Enright & Sherrill' },
];

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeSex = (value: unknown): 'M' | 'F' | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === 'M' || normalized === 'MASCULINO' || normalized === 'MALE') return 'M';
  if (normalized === 'F' || normalized === 'FEMININO' || normalized === 'FEMALE') return 'F';
  return null;
};

const formatClock = (seconds: number): string => {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const getAttemptDistance = (attempt: Tc6AttemptPayload | null | undefined): number => {
  const direct = toFiniteNumber(attempt?.total_distance_m);
  if (direct != null) return direct;
  const perMinute = Array.isArray(attempt?.per_minute_distance_m) ? attempt?.per_minute_distance_m : [];
  const last = perMinute.length ? toFiniteNumber(perMinute[perMinute.length - 1]) : null;
  return last ?? 0;
};

const getLastNonNullNumber = (values: unknown[]): number | null => {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const parsed = toFiniteNumber(values[index]);
    if (parsed != null) return parsed;
  }
  return null;
};

export const formatTc6StopReasonLabel = (reason?: Tc6StopReason | null): string =>
  reason ? STOP_REASON_LABELS[reason] : 'Motivo não informado';

export const normalizeTC6ReferenceKey = (value: unknown): Tc6ReferenceKey => {
  const found = TC6_REFERENCE_OPTIONS.find((option) => option.key === value);
  return found?.key ?? DEFAULT_TC6_REFERENCE_KEY;
};

export const getTC6ReferenceLabel = (value: unknown): string => {
  const normalized = normalizeTC6ReferenceKey(value);
  return TC6_REFERENCE_OPTIONS.find((option) => option.key === normalized)?.label ?? 'Soares & Pereira 2011';
};

export const computeTc6 = (minutes: MinuteData[], corridorLength: number) => {
  const safeCorridor = Math.max(0, Number.isFinite(corridorLength) ? corridorLength : 0);
  const perMinute: number[] = [];
  let totalDistance = 0;
  let prevExtra = 0;

  minutes.forEach((data, index) => {
    const laps = Math.max(0, Number.isFinite(data?.laps) ? data.laps : 0);
    const extraMeters = Math.max(0, Number.isFinite(data?.extraMeters) ? data.extraMeters : 0);
    const extraDiff = Math.max(0, extraMeters - prevExtra);
    const minuteDistance = laps * safeCorridor + extraDiff;
    perMinute[index] = minuteDistance;
    totalDistance += minuteDistance;
    prevExtra = extraMeters;
  });

  const totalMinutes = minutes.length;
  const totalSeconds = totalMinutes * 60;
  const avgSpeed_m_min = totalMinutes > 0 ? totalDistance / totalMinutes : 0;
  const avgSpeed_m_s = totalSeconds > 0 ? totalDistance / totalSeconds : 0;
  const avgSpeed_km_h = avgSpeed_m_min * 0.06;

  return {
    totalDistance,
    perMinute,
    avgSpeed_m_min,
    avgSpeed_m_s,
    avgSpeed_km_h,
  };
};

export const computeTc6DerivedMetrics = (
  distanceMeters: number,
  durationSeconds: number,
  patientSnapshot?: Tc6PatientSnapshot | null,
): Tc6DerivedMetrics | null => {
  if (!distanceMeters || distanceMeters <= 0) return null;

  const age = toFiniteNumber(patientSnapshot?.age);
  const sex = normalizeSex(patientSnapshot?.sex);
  const heightCm = toFiniteNumber(patientSnapshot?.height);
  const weightKg = toFiniteNumber(patientSnapshot?.weight);
  const predictedDistance =
    age != null && sex && heightCm != null && weightKg != null
      ? sex === 'F'
        ? 2.11 * heightCm - 2.29 * weightKg - 5.78 * age + 667
        : 7.57 * heightCm - 5.02 * age - 1.76 * weightKg - 309
      : null;
  const roundedPredicted =
    predictedDistance != null && Number.isFinite(predictedDistance) ? Math.max(0, Math.round(predictedDistance)) : null;
  const percentPredicted =
    roundedPredicted != null && roundedPredicted > 0 ? +((distanceMeters / roundedPredicted) * 100).toFixed(1) : null;
  const actualSeconds = Math.min(Math.max(0, durationSeconds), 360);
  const hours = actualSeconds > 0 ? actualSeconds / 3600 : 0;
  const meanSpeed = hours > 0 ? +((distanceMeters / 1000) / hours).toFixed(2) : null;
  const workKgM = weightKg != null ? +(weightKg * distanceMeters).toFixed(0) : null;
  const workKJ = weightKg != null ? +(((weightKg * distanceMeters * 9.81) / 1000).toFixed(1)) : null;
  const distancePerMinute = actualSeconds > 0 ? +(distanceMeters / (actualSeconds / 60)).toFixed(1) : null;
  const vo2Estimate = distanceMeters > 0 ? +(0.03 * distanceMeters + 3.98).toFixed(1) : null;
  const mets = vo2Estimate != null ? +(vo2Estimate / 3.5).toFixed(1) : null;

  return {
    predictedDistance_m: roundedPredicted,
    predictedFormula: patientSnapshot ? `Enright & Sherrill (${sex === 'F' ? 'mulheres' : 'homens'})` : undefined,
    percentPredicted,
    sixMinuteWork_kgm: workKgM,
    sixMinuteWork_kJ: workKJ,
    meanSpeed_kmh: meanSpeed,
    distancePerMinute_m: distancePerMinute,
    vo2Estimate_mlkgmin: vo2Estimate,
    metsEstimate: mets,
    duration_s: actualSeconds,
  };
};

export const recalculateTc6AttemptPayload = (
  attemptLike: Tc6AttemptPayload | Record<string, any> | null | undefined,
  selectedReferenceKey?: unknown,
): Tc6AttemptPayload | null => {
  if (!attemptLike || typeof attemptLike !== 'object') return null;

  const next = {
    ...attemptLike,
    baseline: {
      ...(attemptLike.baseline && typeof attemptLike.baseline === 'object' ? attemptLike.baseline : {}),
      hr:
        attemptLike.baseline && typeof attemptLike.baseline === 'object' && 'hr' in attemptLike.baseline
          ? attemptLike.baseline.hr
          : null,
      spo2:
        attemptLike.baseline && typeof attemptLike.baseline === 'object' && 'spo2' in attemptLike.baseline
          ? attemptLike.baseline.spo2
          : null,
      bp: {
        sys: null,
        dia: null,
        ...(attemptLike.baseline?.bp && typeof attemptLike.baseline.bp === 'object' ? attemptLike.baseline.bp : {}),
      },
      borg: {
        dyspnea: null,
        fatigue: null,
        ...(attemptLike.baseline?.borg && typeof attemptLike.baseline.borg === 'object' ? attemptLike.baseline.borg : {}),
      },
    },
    final: {
      ...(attemptLike.final && typeof attemptLike.final === 'object' ? attemptLike.final : {}),
      hr:
        attemptLike.final && typeof attemptLike.final === 'object' && 'hr' in attemptLike.final
          ? attemptLike.final.hr
          : null,
      spo2:
        attemptLike.final && typeof attemptLike.final === 'object' && 'spo2' in attemptLike.final
          ? attemptLike.final.spo2
          : null,
      bp: {
        sys: null,
        dia: null,
        ...(attemptLike.final?.bp && typeof attemptLike.final.bp === 'object' ? attemptLike.final.bp : {}),
      },
      borg: {
        dyspnea: null,
        fatigue: null,
        ...(attemptLike.final?.borg && typeof attemptLike.final.borg === 'object' ? attemptLike.final.borg : {}),
      },
    },
    stop_report: {
      total_stopped_s: 0,
      total_stopped_fmt: '00:00',
      intervals: [],
      ...(attemptLike.stop_report && typeof attemptLike.stop_report === 'object' ? attemptLike.stop_report : {}),
    },
    timer: {
      elapsed_s: 360,
      completed: true,
      startedAt: null,
      endedAt: null,
      ...(attemptLike.timer && typeof attemptLike.timer === 'object' ? attemptLike.timer : {}),
    },
  } as Record<string, any>;

  const snapshot =
    next.patientSnapshot && typeof next.patientSnapshot === 'object' ? { ...next.patientSnapshot } : {};
  const age = toFiniteNumber(snapshot.age);
  const sex = normalizeSex(snapshot.sex) ?? (typeof snapshot.sex === 'string' ? snapshot.sex : null);
  const weight = toFiniteNumber(snapshot.weight);
  const height = toFiniteNumber(snapshot.height);
  const bmi = height != null && weight != null && height > 0 ? +(weight / Math.pow(height / 100, 2)).toFixed(1) : null;

  next.patientSnapshot = {
    ...snapshot,
    age,
    sex,
    weight,
    height,
    bmi,
  };

  const normalizedPerMinute = Array.from({ length: 6 }, (_, index) => {
    const entry =
      Array.isArray(next.per_minute) && next.per_minute[index] && typeof next.per_minute[index] === 'object'
        ? next.per_minute[index]
        : {};
    return {
      minute: index + 1,
      hr: entry.hr ?? null,
      spo2: entry.spo2 ?? null,
    };
  });

  const normalizedDistances = Array.from({ length: 6 }, (_, index) => {
    const raw = Array.isArray(next.per_minute_distance_m) ? next.per_minute_distance_m[index] : null;
    return toFiniteNumber(raw);
  });
  const normalizedSpeeds = normalizedDistances.map((value, index) => {
    if (value == null) return null;
    const previous = index > 0 ? normalizedDistances[index - 1] : 0;
    if (previous == null) return null;
    const segment = value - previous;
    return segment >= 0 ? +(segment * 0.06).toFixed(2) : null;
  });

  next.per_minute = normalizedPerMinute;
  next.per_minute_distance_m = normalizedDistances as any;
  next.per_minute_speed_kmh = normalizedSpeeds as any;

  const derivedDuration = toFiniteNumber(next?.derivedMetrics?.duration_s);
  const timerDuration = toFiniteNumber(next?.timer?.elapsed_s);
  const duration = Math.max(1, Math.min(360, timerDuration ?? derivedDuration ?? 360));
  const totalDistance =
    toFiniteNumber(next.total_distance_m)
    ?? getLastNonNullNumber(normalizedDistances)
    ?? 0;

  next.total_distance_m = totalDistance;
  next.corridorLength_m = toFiniteNumber(next.corridorLength_m) ?? 0;
  next.laps = Math.max(0, Math.round(toFiniteNumber(next.laps) ?? 0));
  next.last_lap_meters = toFiniteNumber(next.last_lap_meters) ?? 0;

  const primaryReference = computeTC6PrimaryReference(next as Tc6AttemptPayload, selectedReferenceKey);
  const derivedMetrics = computeTc6DerivedMetrics(totalDistance, duration, next.patientSnapshot);

  next.derivedMetrics = {
    ...(next.derivedMetrics && typeof next.derivedMetrics === 'object' ? next.derivedMetrics : {}),
    ...(derivedMetrics ?? {}),
    predictedDistance_m: primaryReference?.predicted ?? derivedMetrics?.predictedDistance_m ?? null,
    predictedFormula: getTC6ReferenceLabel(selectedReferenceKey),
    percentPredicted: primaryReference?.percentPredicted ?? derivedMetrics?.percentPredicted ?? null,
    duration_s: duration,
  };

  return next as Tc6AttemptPayload;
};

export const recalculateTc6Payload = (payloadLike: unknown): Tc6ResultPayload => {
  const source =
    payloadLike && typeof payloadLike === 'object'
      ? ({ ...(payloadLike as Record<string, any>) } as Record<string, any>)
      : {};
  const selectedReferenceKey = normalizeTC6ReferenceKey(source.referenceEquation);

  const next: Record<string, any> = {
    ...source,
    referenceEquation: selectedReferenceKey,
  };

  next.attempt1 = recalculateTc6AttemptPayload(source.attempt1, selectedReferenceKey) ?? undefined;
  next.attempt2 = recalculateTc6AttemptPayload(source.attempt2, selectedReferenceKey) ?? undefined;

  const attemptCount = next.attempt2 ? 2 : 1;
  next.workflow = {
    status:
      source.workflow && typeof source.workflow === 'object' && source.workflow.status
        ? source.workflow.status
        : 'completed',
    attemptCount,
    savedAt:
      source.workflow && typeof source.workflow === 'object' && typeof source.workflow.savedAt === 'string'
        ? source.workflow.savedAt
        : new Date().toISOString(),
  };

  return next as Tc6ResultPayload;
};

export const getTc6Attempts = (payload: unknown): Tc6AttemptPayload[] => {
  if (!payload || typeof payload !== 'object') return [];
  const source = payload as Tc6ResultPayload;
  return [source.attempt1, source.attempt2].filter(Boolean) as Tc6AttemptPayload[];
};

export const getTc6BestAttempt = (payload: unknown): { attempt: Tc6AttemptPayload | null; index: 1 | 2 | null } => {
  const attempts = getTc6Attempts(payload);
  if (!attempts.length) return { attempt: null, index: null };
  const best = attempts.reduce((currentBest, attempt) => {
    return getAttemptDistance(attempt) > getAttemptDistance(currentBest) ? attempt : currentBest;
  }, attempts[0]);
  const source = payload as Tc6ResultPayload;
  if (source?.attempt2 === best) return { attempt: best, index: 2 };
  return { attempt: best, index: 1 };
};

const buildReferenceResult = (
  key: Tc6ReferenceKey,
  label: string,
  predictedRaw: number | null,
  llnRaw: number | null,
  measuredRaw: number | null,
  llnDescription: string,
): Tc6ReferenceResult => {
  const predicted = predictedRaw != null && Number.isFinite(predictedRaw) ? +predictedRaw.toFixed(1) : null;
  const lln = llnRaw != null && Number.isFinite(llnRaw) ? +llnRaw.toFixed(1) : null;
  const measured = measuredRaw != null && Number.isFinite(measuredRaw) ? measuredRaw : null;
  const percentPredicted =
    predicted != null && predicted > 0 && measured != null ? +((measured / predicted) * 100).toFixed(1) : null;
  const belowLLN = lln != null && measured != null ? measured < lln : null;

  return {
    key,
    label,
    predicted,
    lln,
    percentPredicted,
    belowLLN,
    llnDescription,
  };
};

export const computeTC6PrimaryReference = (
  attempt: Tc6AttemptPayload | null | undefined,
  selectedReferenceKey?: unknown,
): Tc6ReferenceResult | null => {
  if (!attempt) return null;
  const normalizedKey = normalizeTC6ReferenceKey(selectedReferenceKey);
  const references = computeTc6References(attempt, normalizedKey);
  return references.primary;
};

export const computeTc6References = (
  attempt: Tc6AttemptPayload | null | undefined,
  selectedReferenceKey?: Tc6ReferenceKey,
): { primary: Tc6ReferenceResult | null; all: Tc6ReferenceResult[] } => {
  if (!attempt) return { primary: null, all: [] };

  const snapshot = attempt.patientSnapshot || {};
  const distance = toFiniteNumber(attempt.total_distance_m);
  const age = toFiniteNumber(snapshot.age);
  const sex = normalizeSex(snapshot.sex);
  const heightCm = toFiniteNumber(snapshot.height);
  const weightKg = toFiniteNumber(snapshot.weight);
  const heightM = heightCm != null ? heightCm / 100 : null;
  const bmi =
    toFiniteNumber(snapshot.bmi) ??
    (heightCm != null && weightKg != null && heightCm > 0 ? +(weightKg / Math.pow(heightCm / 100, 2)).toFixed(1) : null);
  const baselineHr = toFiniteNumber(attempt.baseline?.hr);
  const finalHr = toFiniteNumber(attempt.final?.hr);
  const deltaHr = baselineHr != null && finalHr != null ? finalHr - baselineHr : null;
  const genderBinary = sex === 'M' ? 1 : sex === 'F' ? 0 : null;

  const enrightPred =
    age != null && heightCm != null && weightKg != null && sex != null
      ? sex === 'M'
        ? 7.57 * heightCm - 5.02 * age - 1.76 * weightKg - 309
        : 2.11 * heightCm - 5.78 * age - 2.29 * weightKg + 667
      : null;
  const enrightLLN = enrightPred != null && sex != null ? enrightPred - (sex === 'M' ? 153 : 139) : null;

  const troostersPred =
    age != null && heightCm != null && weightKg != null && genderBinary != null
      ? 218 + 5.14 * heightCm - 5.32 * age - 1.8 * weightKg + 51.31 * genderBinary
      : null;
  const troostersLLN = troostersPred != null ? troostersPred - 1.645 * 56 : null;

  const iwamaPred = age != null && genderBinary != null ? 622.461 - 1.846 * age + 61.503 * genderBinary : null;
  const iwamaLLN = iwamaPred != null ? iwamaPred - 1.645 * 70.992 : null;

  const douradoPred =
    age != null && weightKg != null && heightM != null && genderBinary != null
      ? 299.296 - 2.728 * age - 2.16 * weightKg + 361.731 * heightM + 56.386 * genderBinary
      : null;
  const douradoLLN = douradoPred != null ? douradoPred - 1.645 * 57.7 : null;

  const soaresPred =
    age != null && bmi != null && heightCm != null
      ? 511 + 0.0066 * Math.pow(heightCm, 2) - 0.03 * Math.pow(age, 2) - 0.068 * Math.pow(bmi, 2)
      : null;
  const soaresLLN = soaresPred != null ? soaresPred * 0.81 : null;

  const britto1Pred =
    age != null && bmi != null && genderBinary != null
      ? 890.46 - 6.11 * age + 0.0345 * Math.pow(age, 2) + 48.87 * genderBinary - 4.87 * bmi
      : null;
  const britto1LLN = britto1Pred != null ? britto1Pred - 1.645 * 77.2 : null;

  const britto2Pred =
    age != null && genderBinary != null && heightCm != null && deltaHr != null
      ? 356.658 - 2.303 * age + 36.648 * genderBinary + 1.704 * heightCm + 1.365 * deltaHr
      : null;
  const britto2LLN = britto2Pred != null ? britto2Pred - 1.645 * 64.3 : null;

  const all = [
    buildReferenceResult('soares_pereira_2011', 'Soares & Pereira 2011', soaresPred, soaresLLN, distance, 'Limite inferior esperado = 81% do previsto'),
    buildReferenceResult('iwama', 'Iwama et al.', iwamaPred, iwamaLLN, distance, 'Limite inferior esperado = previsto - 1.645*SEE'),
    buildReferenceResult('britto_1', 'Britto et al. 1', britto1Pred, britto1LLN, distance, 'Limite inferior esperado = previsto - 1.645*SEE'),
    buildReferenceResult('britto_2', 'Britto et al. 2', britto2Pred, britto2LLN, distance, 'Limite inferior esperado = previsto - 1.645*SEE'),
    buildReferenceResult('dourado', 'Dourado et al.', douradoPred, douradoLLN, distance, 'Limite inferior esperado = previsto - 1.645*SEE'),
    buildReferenceResult('troosters', 'Troosters et al.', troostersPred, troostersLLN, distance, 'Limite inferior esperado = previsto - 1.645*SEE'),
    buildReferenceResult('enright_sherrill', 'Enright & Sherrill', enrightPred, enrightLLN, distance, 'Limite inferior esperado = previsto - 153 (M) / 139 (F)'),
  ];

  const normalizedSelected = normalizeTC6ReferenceKey(selectedReferenceKey);
  const primary =
    all.find((item) => item.key === normalizedSelected) ??
    all.find((item) => item.key === DEFAULT_TC6_REFERENCE_KEY) ??
    null;

  return { primary, all };
};

export const getTc6DistanceSeries = (attempt: Tc6AttemptPayload | null | undefined, selectedReferenceKey?: unknown) => {
  const perMinuteDistance = Array.isArray(attempt?.per_minute_distance_m) ? attempt.per_minute_distance_m : [];
  const cumulative = Array.from({ length: 7 }, (_, index) => (index === 0 ? 0 : Number(perMinuteDistance[index - 1] ?? 0)));
  const primaryReference = computeTC6PrimaryReference(attempt, selectedReferenceKey);
  return cumulative.map((realDistance, minute) => ({
    minute,
    realDistance,
    predictedDistance:
      primaryReference?.predicted != null ? Number(((primaryReference.predicted / 6) * minute).toFixed(1)) : null,
  }));
};

export const getTc6VitalsSeries = (attempt: Tc6AttemptPayload | null | undefined) => {
  const perMinute = Array.isArray(attempt?.per_minute) ? attempt.per_minute : [];
  return Array.from({ length: 7 }, (_, index) => {
    const minuteEntry = index === 0 ? null : perMinute[index - 1];
    return {
      minute: index,
      hr: index === 0 ? toFiniteNumber(attempt?.baseline?.hr) : toFiniteNumber(minuteEntry?.hr),
      spo2: index === 0 ? toFiniteNumber(attempt?.baseline?.spo2) : toFiniteNumber(minuteEntry?.spo2),
    };
  });
};

const buildTc6AutoInterpretationParagraphs = (
  attempt: Tc6AttemptPayload | null | undefined,
  selectedReferenceKey?: unknown,
): string[] => {
  if (!attempt) return [];

  const primaryReference = computeTC6PrimaryReference(attempt, selectedReferenceKey);
  const derived = attempt.derivedMetrics || null;
  const baselineSpo2 = toFiniteNumber(attempt.baseline?.spo2);
  const finalSpo2 = toFiniteNumber(attempt.final?.spo2);
  const perMinuteSpo2 = getTc6VitalsSeries(attempt)
    .map((item) => item.spo2)
    .filter((item): item is number => item != null);
  const minSpo2 = perMinuteSpo2.length ? Math.min(...perMinuteSpo2) : finalSpo2;
  const spo2Drop = baselineSpo2 != null && minSpo2 != null ? +(baselineSpo2 - minSpo2).toFixed(1) : null;
  const baselineHr = toFiniteNumber(attempt.baseline?.hr);
  const finalHr = toFiniteNumber(attempt.final?.hr);
  const stopReport = attempt.stop_report;
  const borgDyStart = toFiniteNumber(attempt.baseline?.borg?.dyspnea);
  const borgDyEnd = toFiniteNumber(attempt.final?.borg?.dyspnea);
  const borgFaStart = toFiniteNumber(attempt.baseline?.borg?.fatigue);
  const borgFaEnd = toFiniteNumber(attempt.final?.borg?.fatigue);
  const distance = getAttemptDistance(attempt);

  const paragraphs: string[] = [];

  const performanceParts: string[] = [];
  performanceParts.push(`distância de ${distance} m`);

  if (primaryReference?.predicted != null) {
    performanceParts.push(`previsto de ${primaryReference.predicted.toFixed(1)} m pela equação ${primaryReference.label}`);
  }
  if (primaryReference?.percentPredicted != null) {
    performanceParts.push(`${primaryReference.percentPredicted.toFixed(1)}% do previsto`);
  }
  if (primaryReference?.lln != null && primaryReference.belowLLN != null) {
    performanceParts.push(
      primaryReference.belowLLN
        ? `abaixo do limite inferior esperado (${primaryReference.lln.toFixed(1)} m)`
        : `dentro da faixa esperada (limite inferior de ${primaryReference.lln.toFixed(1)} m)`,
    );
  }
  if (derived?.meanSpeed_kmh != null) {
    performanceParts.push(`velocidade média de ${derived.meanSpeed_kmh.toFixed(2)} km/h`);
  }
  if (derived?.vo2Estimate_mlkgmin != null) {
    performanceParts.push(`VO2 estimado de ${derived.vo2Estimate_mlkgmin.toFixed(1)} mL/kg/min`);
  } else if (derived?.metsEstimate != null) {
    performanceParts.push(`VO2 estimado de ${derived.metsEstimate.toFixed(1)} METs`);
  }
  if (derived?.sixMinuteWork_kgm != null || derived?.sixMinuteWork_kJ != null) {
    if (derived?.sixMinuteWork_kgm != null && derived?.sixMinuteWork_kJ != null) {
      performanceParts.push(`trabalho estimado de ${derived.sixMinuteWork_kgm} kg·m ou ${derived.sixMinuteWork_kJ} kJ`);
    } else if (derived?.sixMinuteWork_kgm != null) {
      performanceParts.push(`trabalho estimado de ${derived.sixMinuteWork_kgm} kg·m`);
    } else if (derived?.sixMinuteWork_kJ != null) {
      performanceParts.push(`trabalho estimado de ${derived.sixMinuteWork_kJ} kJ`);
    }
  }

  paragraphs.push(`Desempenho global no TC6: ${performanceParts.join(' | ')}.`);

  if (baselineHr != null || finalHr != null) {
    const hrText =
      baselineHr != null && finalHr != null
        ? `FC ${baselineHr.toFixed(0)} -> ${finalHr.toFixed(0)} bpm`
        : baselineHr != null
          ? `FC basal ${baselineHr.toFixed(0)} bpm`
          : `FC final ${finalHr?.toFixed(0)} bpm`;
    paragraphs.push(`Resposta cardiovascular: ${hrText}.`);
  }

  if (minSpo2 != null) {
    const significantDrop = spo2Drop != null && spo2Drop >= 4;
    const text = significantDrop
      ? `houve dessaturação até ${minSpo2.toFixed(1)}% (queda de ${spo2Drop?.toFixed(1)}%)`
      : `a oxigenação manteve-se estável, com mínima de ${minSpo2.toFixed(1)}%`;
    paragraphs.push(`Monitorização de SpO2: ${text}.`);
  }

  if (stopReport?.intervals?.length) {
    paragraphs.push(
      `Foram registradas ${stopReport.intervals.length} parada(s), totalizando ${stopReport.total_stopped_fmt} de tempo interrompido.`,
    );
  } else {
    paragraphs.push('Não houve necessidade de paradas durante o teste.');
  }

  if (borgDyStart != null || borgDyEnd != null || borgFaStart != null || borgFaEnd != null) {
    const formatDelta = (value: number | null) => {
      if (value == null || Number.isNaN(value)) return '—';
      return `${value > 0 ? '+' : ''}${value.toFixed(0)}`;
    };
    const dyDelta = borgDyStart != null && borgDyEnd != null ? borgDyEnd - borgDyStart : null;
    const faDelta = borgFaStart != null && borgFaEnd != null ? borgFaEnd - borgFaStart : null;
    paragraphs.push(
      `Percepção subjetiva de esforço: dispneia ${borgDyStart ?? '—'} -> ${borgDyEnd ?? '—'} (variação ${formatDelta(dyDelta)}) | fadiga ${borgFaStart ?? '—'} -> ${borgFaEnd ?? '—'} (variação ${formatDelta(faDelta)}).`,
    );
  }

  return paragraphs;
};

const sanitizeStoredTc6Interpretation = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value
    .replace(/\r\n/g, '\n')
    .replace(/SpO₂/g, 'SpO2')
    .replace(/VO₂/g, 'VO2')
    .replace(/Δ/g, 'variação')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
};

export const buildTC6AutoInterpretation = (payload: unknown): string => {
  if (!payload || typeof payload !== 'object') return '';
  const normalizedKey = normalizeTC6ReferenceKey((payload as Tc6ResultPayload).referenceEquation);
  const { attempt } = getTc6BestAttempt(payload);
  if (!attempt) return '';
  return sanitizeStoredTc6Interpretation(buildTc6AutoInterpretationParagraphs(attempt, normalizedKey).join('\n\n'));
};

export const resolveTC6Interpretation = (payload: unknown): string => {
  const stored = sanitizeStoredTc6Interpretation((payload as Tc6ResultPayload | null | undefined)?.report?.interpretation);
  if (stored) return stored;
  return buildTC6AutoInterpretation(payload);
};

export const buildTc6Payload = (
  attempt1: Tc6AttemptPayload,
  options?: {
    attempt2?: Tc6AttemptPayload | null;
    selectedReferenceKey?: unknown;
    workflowStatus?: Tc6WorkflowStatus;
    chartImages?: Tc6ChartImages;
    patientContext?: Record<string, unknown>;
    professionalContext?: Record<string, unknown>;
  },
): Tc6ResultPayload & { chartImages?: Tc6ChartImages } => {
  const attempt2 = options?.attempt2 ?? null;
  const workflowStatus = options?.workflowStatus ?? 'completed';
  const selectedReferenceKey = normalizeTC6ReferenceKey(options?.selectedReferenceKey);
  const basePayload: Tc6ResultPayload = {
    referenceEquation: selectedReferenceKey,
    workflow: {
      status: workflowStatus,
      attemptCount: attempt2 ? 2 : 1,
      savedAt: new Date().toISOString(),
    },
    attempt1,
    ...(attempt2 ? { attempt2 } : {}),
    finishedAt: new Date().toISOString(),
    ...(options?.patientContext ? { patientContext: options.patientContext } : {}),
    ...(options?.professionalContext ? { professionalContext: options.professionalContext } : {}),
  };
  const interpretation = buildTC6AutoInterpretation(basePayload);
  const withReport = interpretation ? { ...basePayload, report: { interpretation } } : basePayload;
  return options?.chartImages && Object.keys(options.chartImages).length
    ? { ...withReport, chartImages: options.chartImages }
    : withReport;
};

export const isTc6WorkflowCompleted = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const workflowStatus = (payload as Tc6ResultPayload).workflow?.status;
  return workflowStatus === 'completed';
};

export const isTc6AwaitingSecondAttempt = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  return (payload as Tc6ResultPayload).workflow?.status === 'awaiting_second_attempt';
};

export const summarizeTc6Result = (payload: unknown): Tc6Summary => {
  const normalizedPayload =
    payload && typeof payload === 'object' ? (payload as Tc6ResultPayload) : ({ referenceEquation: DEFAULT_TC6_REFERENCE_KEY } as Tc6ResultPayload);
  const selectedReferenceKey = normalizeTC6ReferenceKey(normalizedPayload.referenceEquation);
  const selectedReferenceLabel = getTC6ReferenceLabel(selectedReferenceKey);
  const attempts = getTc6Attempts(normalizedPayload);
  const best = getTc6BestAttempt(normalizedPayload);
  const primaryReference = computeTC6PrimaryReference(best.attempt, selectedReferenceKey);
  const vitalsSeries = getTc6VitalsSeries(best.attempt);
  const spo2Values = vitalsSeries.map((item) => item.spo2).filter((item): item is number => item != null);
  const minSpo2 = spo2Values.length ? Math.min(...spo2Values) : null;
  const baselineSpo2 = toFiniteNumber(best.attempt?.baseline?.spo2);
  const spo2Drop = baselineSpo2 != null && minSpo2 != null ? +(baselineSpo2 - minSpo2).toFixed(1) : null;
  const totalStopped_s = best.attempt?.stop_report?.total_stopped_s ?? null;
  const percentPredicted =
    primaryReference?.percentPredicted ?? best.attempt?.derivedMetrics?.percentPredicted ?? null;
  const severity =
    percentPredicted == null
      ? 'Capacidade não comparada'
      : percentPredicted >= 90
        ? 'Capacidade preservada'
        : percentPredicted >= 75
          ? 'Redução leve'
          : percentPredicted >= 60
            ? 'Redução moderada'
            : 'Redução grave';

  return {
    attemptCount: normalizedPayload.workflow?.attemptCount ?? (attempts.length >= 2 ? 2 : 1),
    workflowStatus: normalizedPayload.workflow?.status ?? 'completed',
    bestAttempt: best.index,
    bestDistance_m: best.attempt ? getAttemptDistance(best.attempt) : null,
    selectedReferenceKey,
    selectedReferenceLabel,
    predictedDistance_m: primaryReference?.predicted ?? best.attempt?.derivedMetrics?.predictedDistance_m ?? null,
    percentPredicted,
    meanSpeed_kmh: best.attempt?.derivedMetrics?.meanSpeed_kmh ?? null,
    minSpo2,
    spo2Drop,
    totalStopped_s,
    hasSignificantDesaturation: Boolean(spo2Drop != null && spo2Drop >= 4) || Boolean(minSpo2 != null && minSpo2 < 85),
    severity,
  };
};
